// =====================================================================
// Assessment Center — Instructor Panel
// Question Bank page — browse, filter, edit, and delete questions.
// =====================================================================
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Input,
  PageHeader,
  Select,
  Spinner,
  statusTone,
} from "../../../shared/components/ui";
import {
  deleteQuestion,
  listQuestionBanks,
  listQuestions,
  type QuestionWithDetail,
} from "../lib/mock-api";
import type { QuestionBank, QuestionStatus, QuestionType } from "../../../shared/types/db";

const typeLabel: Record<QuestionType, string> = {
  multiple_choice: "Multiple Choice",
  essay: "Essay",
};

export default function QuestionBankPage() {
  const [banks, setBanks] = useState<QuestionBank[]>([]);
  const [questions, setQuestions] = useState<QuestionWithDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<QuestionWithDetail | null>(null);

  const [bankId, setBankId] = useState("");
  const [type, setType] = useState<QuestionType | "">("");
  const [status, setStatus] = useState<QuestionStatus | "">("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    listQuestionBanks().then(setBanks);
  }, []);

  async function refresh() {
    setLoading(true);
    const data = await listQuestions({
      bankId: bankId || undefined,
      type: type || undefined,
      status: status || undefined,
      search: search || undefined,
    });
    setQuestions(data);
    setLoading(false);
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankId, type, status, search]);

  const bankName = useMemo(() => {
    const map = new Map(banks.map((b) => [b.id, b.title]));
    return (id: string) => map.get(id) ?? "—";
  }, [banks]);

  async function confirmDelete() {
    if (!pendingDelete) return;
    try {
      await deleteQuestion(pendingDelete.id);
      setPendingDelete(null);
      refresh();
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : "Couldn't delete this question.");
      setPendingDelete(null);
    }
  }

  return (
    <div>
      <PageHeader
        title="Question Bank"
        subtitle="Every question you've authored, across all banks."
        action={
          <Link to="/instructor/assessment-center/questions/new">
            <Button>+ Create Question</Button>
          </Link>
        }
      />

      {deleteError && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{deleteError}</span>
          <button onClick={() => setDeleteError(null)} aria-label="Dismiss">
            ✕
          </button>
        </div>
      )}

      <Card className="mb-5 flex flex-wrap items-center gap-3 p-4">
        <Input
          placeholder="Search prompts…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <Select value={bankId} onChange={(e) => setBankId(e.target.value)} className="max-w-[220px]">
          <option value="">All banks</option>
          {banks.map((b) => (
            <option key={b.id} value={b.id}>
              {b.title}
            </option>
          ))}
        </Select>
        <Select
          value={type}
          onChange={(e) => setType(e.target.value as QuestionType | "")}
          className="max-w-[180px]"
        >
          <option value="">All types</option>
          <option value="multiple_choice">Multiple Choice</option>
          <option value="essay">Essay</option>
        </Select>
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value as QuestionStatus | "")}
          className="max-w-[160px]"
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </Select>
      </Card>

      {loading ? (
        <div className="flex items-center gap-2 py-16 text-slate-500">
          <Spinner /> Loading questions…
        </div>
      ) : questions.length === 0 ? (
        <EmptyState
          title="No questions match these filters"
          description="Try clearing a filter, or create your first question for this bank."
          action={
            <Link to="/instructor/assessment-center/questions/new">
              <Button>+ Create Question</Button>
            </Link>
          }
        />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="bg-navy-50/60 text-xs uppercase tracking-wide text-navy-700">
              <tr>
                <th className="px-4 py-3 font-medium">Prompt</th>
                <th className="px-4 py-3 font-medium">Bank</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Difficulty</th>
                <th className="px-4 py-3 font-medium">Points</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {questions.map((q) => (
                <tr key={q.id} className="border-t border-border-soft hover:bg-navy-50/30">
                  <td className="max-w-sm px-4 py-3 text-slate-700">
                    <span className="line-clamp-2">{q.prompt}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{bankName(q.question_bank_id)}</td>
                  <td className="px-4 py-3 text-slate-500">{typeLabel[q.type]}</td>
                  <td className="px-4 py-3 capitalize text-slate-500">{q.difficulty}</td>
                  <td className="px-4 py-3 text-slate-500">{q.default_points}</td>
                  <td className="px-4 py-3">
                    <Badge tone={statusTone(q.status)}>{q.status}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <Link to={`/instructor/assessment-center/questions/${q.id}/edit`}>
                        <Button variant="ghost" className="px-2 py-1">
                          Edit
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        className="px-2 py-1 text-red-600 hover:bg-red-50"
                        onClick={() => setPendingDelete(q)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete question"
        description={`This permanently deletes "${pendingDelete?.prompt.slice(0, 80) ?? ""}${
          (pendingDelete?.prompt.length ?? 0) > 80 ? "…" : ""
        }". This can't be undone.`}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
